// Home.js - Revamped hero-focused marketing page

import React, { useEffect, useRef, useState } from 'react'; // Added useState
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaStethoscope,
  FaCheckCircle,
  FaClipboardList,
  FaGraduationCap,
  FaFeatherAlt,
  FaChevronDown,
  FaUniversity,
  FaHospital,
  FaCompass,
  FaUserMd,
  FaSyringe
} from 'react-icons/fa';
import '../css/App.css';
import PathfinderIntroOverlay from '../components/features/PathfinderModules/PathfinderIntroOverlay';
import '../css/HomeStyles.css';

// Pathfinder screenshots (ensure these PNGs exist in /src/assets/pathfinder/)
import pfMid from '../assets/pathfinder/pf_middle_view_v2.png';
import pfDeep from '../assets/pathfinder/PathFinderSteps_whiteBG.png';
import pfTask from '../assets/pathfinder/pf_task_modal_v3.png';

const Home = () => {
  // State for the intro overlay
  const [showIntroOverlay, setShowIntroOverlay] = useState(false);
  
  const animatedRefs = useRef([]);

  const addAnimateRef = (el) => {
    if (el && !animatedRefs.current.includes(el)) {
      animatedRefs.current.push(el);
    }
  };

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('mm-home-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -5% 0px' }
    );

    animatedRefs.current.forEach(el => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="mm-home-root">
      {/* Mobile-specific fix for zig-zag sections stacking vertically */}
      <style>
        {`
          @media (max-width: 992px) {
            .mm-home-zig, 
            .mm-home-zig.reverse {
              flex-direction: column !important;
              text-align: center;
              padding: 3rem 5% !important;
              gap: 2rem !important;
              display: flex !important;
              align-items: center !important;
            }
            .mm-home-zig-media, 
            .mm-home-zig-copy {
              width: 100% !important;
              max-width: 100% !important;
              flex: none !important;
            }
            .mm-home-screenshot {
              width: 100% !important;
              margin: 0 !important;
            }
            .mm-home-screenshot img {
              max-width: 100% !important;
              height: auto !important;
              object-fit: contain;
            }
            /* Ensure the text is below the image on mobile */
            .mm-home-zig.reverse {
               display: flex !important;
               flex-direction: column !important;
            }
            .mm-home-zig-media {
              order: 1;
            }
            .mm-home-zig-copy {
              order: 2;
            }
          }
        `}
      </style>
      {/* HERO */}
      <section className="mm-home-hero" ref={addAnimateRef}>
        <div className="mm-home-hero-bg-orb" aria-hidden="true" />
        <div className="mm-home-hero-inner">
          <div className="mm-home-hero-copy">
            <h1 className="mm-home-hero-title">
              Build a Competitive <span className="mm-home-gradient-text">UK Medical Specialty Portfolio</span>
            </h1>
            <ul className="mm-home-trust-list" aria-label="Platform highlights">
              <li><FaCheckCircle /> Aligned to UK Portfolio Criteria</li>
              <li><FaCheckCircle /> Surgical and Medical Pathways </li>

            </ul>
            <p className="mm-home-hero-sub">
              MedMap is a career development platform that provides the optimum path to your Dream Specialty. MedMap provides an interactive step-by-step guide to maximising points on your upcoming application. 
            </p>

            <div className="mm-home-cta-cluster">
              <Link to="/dream-specialty" className="mm-home-cta-primary" aria-label="Begin your journey quiz">
                Begin your Journey <FaArrowRight />
              </Link>
            </div>

          </div>
          <div className="mm-home-hero-visual" aria-hidden="true">
            <div className="mm-home-hero-ring ring-1" />
            <div className="mm-home-hero-ring ring-2" />
            <div className="mm-home-floating-card fc-1">
              <FaHospital/>
              <span>Core Surgical Training</span>
            </div>
            <div className="mm-home-floating-card fc-2">
              <FaStethoscope />
              <span>Internal Medicine Training</span>
            </div>
            <div className="mm-home-floating-card fc-3">
              <FaSyringe/>
              <span>Acute Care Common Stem</span>
            </div>
          </div>
        </div>
        <div className="mm-home-scroll-hint">
          <span className="mm-home-scroll-icon"><FaChevronDown /></span>
          <span className="mm-home-scroll-text">Scroll</span>
        </div>
        <div className="mm-home-hero-fade" aria-hidden="true" />
      </section>

      {/* ZIG-ZAG SECTION 1 */}
      <section className="mm-home-zig" ref={addAnimateRef}>
        <div className="mm-home-zig-media">
          <figure className="mm-home-screenshot">
            <img src={pfMid} alt="Pathfinder middle view showing domain progress overview" loading="lazy" />
            {/* <figcaption>Pathfinder Middle View – Balanced domain progress view.</figcaption> */}
          </figure>
        </div>
        <div className="mm-home-zig-copy">
            <h2 className="mm-home-zig-title"> Track Your Application Readiness</h2>
            <p>
              MedMap "Pathfinder" tracks your current progress against official UK specialty application criteria by domain.
            </p>
            {/* <ul className="mm-home-feature-bullets">
              <li><FaCheckCircle /> Domain scoring mapped to official criteria</li>
              <li><FaCheckCircle /> Visual thresholds for interview readiness</li>
              <li><FaCheckCircle /> Intelligent progression weighting</li>
            </ul> */}
            
            {/* Replaced Link with Button to open Overlay */}
            <button 
              type="button" 
              className="mm-home-cta-secondary ghost"
              onClick={() => setShowIntroOverlay(true)}
              style={{
                background: 'transparent',
                border: '2px solid currentColor',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                fontWeight: '600',
                padding: '0.8rem 1.5rem',
                borderRadius: '8px',
                display: 'inline-block'
              }}
            >
              Explore Pathfinder
            </button>
        </div>
      </section>

      {/* ZIG-ZAG SECTION 2 (reverse) */}
      <section className="mm-home-zig reverse" ref={addAnimateRef}>
        <div className="mm-home-zig-media">
          <figure className="mm-home-screenshot">
            <img src={pfDeep} alt="Pathfinder deep view with expanded actionable tasks" loading="lazy" />
            {/* <figcaption>Deep View – Expand domains into structured micro-interventions.</figcaption> */}
          </figure>
        </div>
        <div className="mm-home-zig-copy">
          <h2 className="mm-home-zig-title">Step by Step Progress</h2>
          <p>
            MedMap Pathfinder breaks down application requirements that score maximum points into SMART tasks.
        
          </p>
          {/* <ul className="mm-home-feature-bullets">
            <li><FaCheckCircle /> Structured micro-tasks & checkpoints</li>
            <li><FaCheckCircle /> Progress persistence & adaptive focus</li>
            <li><FaCheckCircle /> Cognitive load reduction through staged reveal</li>
          </ul> */}
          <Link to="/dream-specialty" className="mm-home-cta-primary inline">
            Begin your Journey <FaArrowRight />
          </Link>
        </div>
      </section>

      {/* ZIG-ZAG SECTION 3 */}
      <section className="mm-home-zig" ref={addAnimateRef}>
        <div className="mm-home-zig-media">
          <figure className="mm-home-screenshot">
            <img src={pfTask} alt="Pathfinder task modal showing detailed requirements and steps" loading="lazy" />
            {/* <figcaption>Task Modal – High-resolution clarity for execution.</figcaption> */}
          </figure>
        </div>
        <div className="mm-home-zig-copy">
          <h2 className="mm-home-zig-title">Deep Dive on Each Step</h2>
          <p>
            All resources and a detailed roadmap to complete each individual task ensuring you are never stuck. 
          </p>
          <ul className="mm-home-feature-bullets">
            {/* <li><FaCheckCircle /> Requirements & rationale displayed together</li>
            <li><FaCheckCircle /> Clear steps to SMART execution  </li>
            <li><FaCheckCircle /> Frequent pitfalls clearly answered</li> */}
          </ul>
        </div>
      </section>

      {/* UK ALIGNMENT */}
      <section className="mm-home-uk" ref={addAnimateRef}>
        <div className="mm-home-uk-inner">
          <h2 className="mm-home-section-title">Aligned to UK Specialty Application Frameworks</h2>
          <p className="mm-home-section-sub">
            Designed around UK application scoring matrices (e.g., CST, IMT, ACCS). MedMap collates
            assessment domains into a clear progression pathway so you always know where you stand, and where you're heading.
          </p>
          <div className="mm-home-uk-grid">
            <div className="mm-home-uk-card">
              <FaClipboardList className="mm-home-uk-icon" />
              <h3>Matrix Parsing</h3>
              <p>Key portfolio domains translated into interactive progress variables.</p>
            </div>
            <div className="mm-home-uk-card">
              <FaGraduationCap className="mm-home-uk-icon" />
              <h3>Academic & Leadership</h3>
              <p>Research, QI & leadership trajectories tracked proportionally—not pass/fail.</p>
            </div>
            <div className="mm-home-uk-card">
              <FaCompass className="mm-home-uk-icon" />
              <h3>Holistic Emphasis</h3>
              <p>Highlights focus toward weaker domains while maintaining longitudinal growth.</p>
            </div>
            <div className="mm-home-uk-card">
              <FaFeatherAlt className="mm-home-uk-icon" />
              <h3>Minimal Cognitive Drag</h3>
              <p>Beautiful simplistic design minimizes cognitive load.</p>
            </div>
          </div>
          <div className="mm-home-inline-cta">
            <Link to="/dream-specialty" className="mm-home-cta-primary">
              Begin your Journey <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mm-home-final" ref={addAnimateRef}>
        <div className="mm-home-final-inner">
          <h2>Turn Ambition Into Structured Momentum</h2>
          <p>
            Start today. The clarity you gain in the next 5 minutes compounds across years of training.
          </p>
          <Link to="/dream-specialty" className="mm-home-cta-primary large">
            Begin your Journey <FaArrowRight />
          </Link>
        </div>
      </section>

      {/* Render the Overlay when active */}
      {showIntroOverlay && (
        <PathfinderIntroOverlay 
          open={true} 
          onClose={() => setShowIntroOverlay(false)} 
        />
      )}
    </div>
  );
};

export default Home;



